import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, ShieldAlert, Copy, Check, Crown, Gift } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

export default function TwoFASection() {
  const [status, setStatus] = useState({ enabled: false, backup_codes_total: 0, backup_codes_remaining: 0, premium_bonus_eligible: false });
  const [loading, setLoading] = useState(false);
  // Setup wizard state
  const [setupOpen, setSetupOpen] = useState(false);
  const [qr, setQr] = useState(null); // {qr_data_url, secret}
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null); // shown once after activation
  const [bonus, setBonus] = useState(null); // {granted, days, premium_until}
  const [copied, setCopied] = useState(false);
  // Disable dialog
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/security/2fa/status");
      setStatus(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/security/2fa/setup");
      setQr(data);
      setCode("");
      setBackupCodes(null);
      setSetupOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossible de démarrer la configuration");
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/security/2fa/verify", { code });
      setBackupCodes(data.backup_codes || []);
      setBonus(data.premium_bonus || null);
      if (data?.premium_bonus?.granted) {
        toast.success(`+ ${data.premium_bonus.days} jours Premium offerts !`, { duration: 5000 });
      } else {
        toast.success("2FA activée ! Note bien tes codes de secours.");
      }
      await loadStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const closeSetup = () => {
    setSetupOpen(false);
    setQr(null);
    setCode("");
    setBackupCodes(null);
    setBonus(null);
  };

  const disable2FA = async () => {
    if (!disablePwd) { toast.error("Mot de passe requis"); return; }
    if (!disableCode || disableCode.length < 6) { toast.error("Code 2FA requis"); return; }
    setLoading(true);
    try {
      await api.post("/security/2fa/disable", { password: disablePwd, code: disableCode });
      toast.success("2FA désactivée");
      setDisableOpen(false);
      setDisablePwd("");
      setDisableCode("");
      await loadStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Désactivation impossible");
    } finally {
      setLoading(false);
    }
  };

  const copyCodes = async () => {
    if (!backupCodes) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      setCopied(true);
      toast.success("Codes copiés");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copie impossible");
    }
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-6"
      data-testid="twofa-section"
    >
      {/* Bonus teaser shown only if 2FA not enabled and bonus still eligible */}
      {!status.enabled && status.premium_bonus_eligible && (
        <div
          className="mb-5 flex items-center gap-3 rounded-xl bg-gradient-to-r from-yellow-50 to-amber-50 border border-amber-200 px-4 py-3"
          data-testid="twofa-bonus-teaser"
        >
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 text-white flex items-center justify-center shrink-0 shadow">
            <Gift size={18} />
          </div>
          <div className="min-w-0 text-sm">
            <p className="font-semibold text-amber-900">
              🎁 Active la 2FA et reçois <span className="underline decoration-2 decoration-amber-400">1 mois Premium offert</span>
            </p>
            <p className="text-amber-800/80 text-xs mt-0.5">
              Offre unique, valable une seule fois. Tu garderas l'accès Premium même après désactivation de la 2FA.
            </p>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${status.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
        >
          {status.enabled ? <ShieldCheck size={18} /> : <ShieldAlert size={18} />}
        </div>
        <div>
          <p className="font-medium text-slate-900">
            Vérification en 2 étapes (2FA)
            {status.enabled && (
              <span className="ml-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="twofa-enabled-pill">
                Activée
              </span>
            )}
          </p>
          <p className="text-sm text-slate-500 mt-0.5">
            {status.enabled
              ? `Codes de secours restants : ${status.backup_codes_remaining}/${status.backup_codes_total}. À chaque connexion, un code de ton appli sera demandé.`
              : "Protège ton compte avec un code temporaire (Google Authenticator, 1Password, Authy…)."}
          </p>
        </div>
      </div>
      {status.enabled ? (
        <Button
          variant="outline"
          className="shrink-0 text-red-600 hover:text-red-700 hover:border-red-200"
          onClick={() => setDisableOpen(true)}
          data-testid="twofa-disable-btn"
        >
          Désactiver
        </Button>
      ) : (
        <Button
          className="shrink-0 bg-emerald-600 hover:bg-emerald-700"
          onClick={startSetup}
          disabled={loading}
          data-testid="twofa-setup-btn"
        >
          {loading ? "..." : "Activer la 2FA"}
        </Button>
      )}
      </div>

      {/* Setup wizard */}
      <Dialog open={setupOpen} onOpenChange={(o) => (o ? setSetupOpen(true) : closeSetup())}>
        <DialogContent data-testid="twofa-setup-dialog">
          <DialogHeader>
            <DialogTitle>Activer la 2FA</DialogTitle>
            <DialogDescription>
              {backupCodes
                ? "Note ces codes de secours dans un endroit sûr. Ils servent si tu perds ton téléphone. Chacun ne fonctionne qu'une seule fois."
                : "1. Scanne ce QR code avec ton appli d'authentification. 2. Entre le code à 6 chiffres affiché."}
            </DialogDescription>
          </DialogHeader>
          {!backupCodes && qr && (
            <form onSubmit={verifySetup} className="space-y-4">
              <div className="flex justify-center">
                <img src={qr.qr_data_url} alt="QR code 2FA" className="w-48 h-48 border border-slate-200 rounded-lg" data-testid="twofa-qr-image" />
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-1">Ou saisis cette clé manuellement :</p>
                <p className="font-mono text-xs text-slate-700 break-all bg-slate-50 px-3 py-2 rounded border border-slate-200" data-testid="twofa-secret-text">
                  {qr.secret}
                </p>
              </div>
              <div>
                <Label htmlFor="twofa-verify-code" className="text-xs">Code à 6 chiffres</Label>
                <Input
                  id="twofa-verify-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  data-testid="twofa-verify-code-input"
                  className="mt-1.5 tracking-widest text-center font-mono text-lg"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeSetup}>Annuler</Button>
                <Button type="submit" disabled={loading || code.length !== 6} data-testid="twofa-verify-submit-btn">
                  {loading ? "Vérification..." : "Activer"}
                </Button>
              </DialogFooter>
            </form>
          )}
          {backupCodes && (
            <div className="space-y-4">
              {bonus?.granted && (
                <div
                  className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-yellow-50 to-amber-50 border border-amber-200 px-4 py-3"
                  data-testid="twofa-bonus-confirmation"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 text-white flex items-center justify-center shrink-0 shadow">
                    <Crown size={20} />
                  </div>
                  <div className="min-w-0 text-sm">
                    <p className="font-semibold text-amber-900">+ {bonus.days} jours Premium offerts ! 🎉</p>
                    {bonus.premium_until && (
                      <p className="text-amber-800/80 text-xs">
                        Valable jusqu'au {new Date(bonus.premium_until).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="twofa-backup-codes-grid">
                {backupCodes.map((c) => (
                  <code key={c} className="font-mono text-sm text-slate-900 text-center">{c}</code>
                ))}
              </div>
              <Button type="button" variant="outline" onClick={copyCodes} className="w-full" data-testid="twofa-copy-codes">
                {copied ? <><Check size={14} className="mr-1.5" /> Copiés !</> : <><Copy size={14} className="mr-1.5" /> Copier les 8 codes</>}
              </Button>
              <DialogFooter>
                <Button onClick={closeSetup} data-testid="twofa-done-btn">J'ai noté mes codes</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent data-testid="twofa-disable-dialog">
          <DialogHeader>
            <DialogTitle>Désactiver la 2FA</DialogTitle>
            <DialogDescription>Confirme ton mot de passe ET un code 2FA pour désactiver.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="2fa-disable-pwd" className="text-xs">Mot de passe</Label>
              <PasswordInput id="2fa-disable-pwd" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} data-testid="twofa-disable-pwd-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="2fa-disable-code" className="text-xs">Code 2FA à 6 chiffres</Label>
              <Input
                id="2fa-disable-code"
                inputMode="numeric"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                data-testid="twofa-disable-code-input"
                className="mt-1.5 font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDisableOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={disable2FA} disabled={loading} data-testid="twofa-disable-confirm-btn">
              {loading ? "..." : "Désactiver la 2FA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
